import { useState, useEffect, useRef } from 'react';
import './SelloAnimado.css';
import { esDieciocho } from '../lib/dieciocho';

/**
 * SelloAnimado — pieza de identidad MiPAE (v3)
 *
 * Animación de ~3 segundos:
 *   1) Las dos figuras (cuidadora navy + persona cuidada azul medio)
 *      aparecen con fade + leve scale-up.
 *   2) Una línea fina cruza bajo ellas de izquierda a derecha.
 *   3) El wordmark "MiPAE" se escribe letra por letra (2026-09-18: "que el
 *      MiPAE del medio quede con la tipografía del Baloo... pensemos en otra
 *      animación ahí"). Antes bajaba entero como un bloque: el movimiento no
 *      decía nada y contradecía a la firma que viene justo después, que SÍ se
 *      escribe. Ahora las dos mitades de la pieza cuentan lo mismo, la palabra
 *      y la firma, y la letra es la de la MARCA.
 *   4) La firma personal de Sebastián (".-" manuscrita) se completa
 *      en dos tiempos: primero el punto aparece como toque de pluma,
 *      después el trazo se dibuja de izquierda a derecha.
 *
 * Props:
 *  - size:        número en px o string CSS válido (default: 200)
 *  - duration:    duración total en ms (default: 3000)
 *  - background:  color de fondo del contenedor (default: transparent)
 *  - autoPlay:    si false, muestra el frame final estático (default: true)
 *  - onComplete:  callback opcional al terminar la animación
 */
const SelloAnimado = ({
  size = 200,
  duration = 3000,
  background = 'transparent',
  autoPlay = true,
  onComplete,
  className = '',
  ...rest
}) => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mql.matches);
    const handler = (e) => setPrefersReducedMotion(e.matches);
    mql.addEventListener?.('change', handler);
    return () => mql.removeEventListener?.('change', handler);
  }, []);

  useEffect(() => {
    if (!autoPlay || prefersReducedMotion || !onComplete) return;
    const t = setTimeout(onComplete, duration);
    return () => clearTimeout(t);
  }, [autoPlay, prefersReducedMotion, duration, onComplete]);

  const shouldAnimate = autoPlay && !prefersReducedMotion;
  // Una sola lectura de la fecha por render (mismo criterio que LockupHeader).
  const dieciocho = esDieciocho();
  const sizeCSS = typeof size === 'number' ? `${size}px` : size;

  return (
    <div
      ref={containerRef}
      className={`mipae-sello ${shouldAnimate ? 'mipae-sello--animate' : 'mipae-sello--static'} ${className}`}
      style={{
        width: sizeCSS,
        height: sizeCSS,
        background,
        '--mipae-sello-duration': `${duration}ms`,
      }}
      role="img"
      aria-label="MiPAE, tu compañero en enfermería"
      {...rest}
    >
      <svg
        viewBox="0 0 1080 1080"
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
        aria-hidden="true"
      >
        {/* Los colores salen de variables desde el 2026-09-19. Estaban
            clavados en hex, así que el sello no tenía modo oscuro: se pintaba
            navy #142B4D sobre el papel oscuro #070D1A, con contraste 1.37:1,
            o sea invisible. Y esta es la PANTALLA DE CARGA de la app
            (App.jsx) más la landing, o sea lo primero que ve cualquiera que
            abre MiPAE en oscuro, en todos los temas, el default incluido.
            El lockup del encabezado ya había resuelto lo mismo el 2026-09-08;
            al sello nunca le llegó el arreglo. El claro no cambió ni un tono:
            las variables conservan los hex de siempre. */}
        {/* Figura principal (navy) */}
        <g className="mipae-sello__figura-principal">
          <path
            d="M 385 405 L 540 405 L 540 565 Q 540 595, 510 595 L 415 595 Q 385 595, 385 565 Z"
            fill="var(--sello-principal)"
          />
          <circle cx="462" cy="320" r="60" fill="var(--sello-principal)" />
        </g>

        {/* Figura acompañante (azul medio) */}
        <g className="mipae-sello__figura-acompanante">
          <path
            d="M 555 440 L 685 440 L 685 565 Q 685 595, 655 595 L 585 595 Q 555 595, 555 565 Z"
            fill="var(--sello-acompanante)"
          />
          <circle cx="620" cy="370" r="48" fill="var(--sello-acompanante)" />
        </g>

        {/* Línea separadora */}
        <line
          className="mipae-sello__linea"
          x1="425" y1="623" x2="640" y2="623"
          stroke="var(--sello-linea)" strokeWidth="5" strokeLinecap="round"
        />

        {/* Wordmark MiPAE en Baloo 2, la letra de la marca (pedido del
            fundador 2026-09-18). Antes leía `--fuente-serif`, o sea seguía al
            tipo de letra ELEGIBLE por el usuario: el mismo logo se veía de
            cinco maneras distintas y no coincidía con el wordmark del header.
            El wordmark es MARCA, no ambiente, así que ningún tema ni la letra
            elegida lo mueven: misma cadena de respaldo que .lockup-wordmark.
            fontFamily va por style y no como atributo SVG porque los atributos
            SVG no entienden var().
            Cada letra es un tspan propio para poder encenderlas en cascada
            (opacity, nada de transform: `transform` sobre <tspan> no es
            confiable fuera de Chromium). Van en tspans y no en <text> sueltos
            con x propia para no depender de las métricas de la fuente: el
            navegador arma la palabra, nosotros solo encendemos las letras. */}
        <text
          className="mipae-sello__wordmark"
          x="540" y="790"
          textAnchor="middle"
          style={{ fontFamily: "'Baloo 2', var(--fuente-sans, Inter), system-ui, sans-serif" }}
          fontSize="150"
          fontWeight="700"
          fill="var(--sello-principal)"
          letterSpacing="-2"
        >
          {['M', 'i', 'P', 'A', 'E'].map((letra, i) => (
            <tspan key={i} className="mipae-sello__letra">{letra}</tspan>
          ))}
        </text>

        {/* Firma manuscrita: punto + trazo curvo */}
        <circle
          className="mipae-sello__firma-punto"
          cx="800" cy="790" r="8"
          fill="var(--sello-principal)"
        />
        {/* El Dieciocho el trazo de la firma FLAMEA como bandera, igual que
            el del header (lockup-trazo-flamea): la misma firma, el mismo
            gesto, en las dos superficies donde vive. */}
        <path
          className={'mipae-sello__firma-trazo' + (dieciocho ? ' mipae-sello__firma-trazo--flamea' : '')}
          d="M 820 793 Q 845 788, 870 791 T 905 790"
          stroke="var(--sello-principal)"
          strokeWidth="7"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
    </div>
  );
};

export default SelloAnimado;
